/**
 * Suspension Placement Logic (GRE-113)
 *
 * Calculates which voice can create a valid suspension
 * between two chords, and determines the preparation,
 * suspension, and resolution notes.
 */

import { Note, Interval } from 'tonal';
import type { Chord, Voices } from '@/types';
import type {
  SuspensionType,
  SuspensionVoice,
  SuspensionPlacement,
  SuspensionValidation,
} from '@/types/suspension-connector';
import { SUSPENSION_CONFIGS } from '@/types/suspension-connector';

/**
 * Voice parts in order of preference (soprano most audible)
 */
const VOICE_PREFERENCE: SuspensionVoice[] = ['soprano', 'alto', 'tenor', 'bass'];

/**
 * Get MIDI number for a note (e.g., "C4" -> 60)
 */
function getMidi(note: string): number {
  return Note.midi(note) ?? 0;
}

/**
 * Get interval in semitones between two notes
 */
function getIntervalSemitones(lower: string, upper: string): number {
  const lowerMidi = getMidi(lower);
  const upperMidi = getMidi(upper);
  return ((upperMidi - lowerMidi) % 12 + 12) % 12;  // Normalize to 0-11
}

/**
 * Get the note a step down from the given note
 */
function stepDown(note: string, halfSteps: number = 1): string {
  const transposed = Note.transpose(note, Interval.fromSemitones(-halfSteps));
  return transposed || note;
}

/**
 * Get the note a step up from the given note
 */
function stepUp(note: string, halfSteps: number = 1): string {
  const transposed = Note.transpose(note, Interval.fromSemitones(halfSteps));
  return transposed || note;
}

/**
 * Check if a note exists in the target chord's voices
 */
function noteInChord(note: string, voices: Voices): boolean {
  const noteClass = Note.pitchClass(note);
  return Object.values(voices).some(v => Note.pitchClass(v) === noteClass);
}

/**
 * Find which voice in target chord contains a note matching the pitch class
 */
export function findVoiceWithNote(note: string, voices: Voices): SuspensionVoice | null {
  const noteClass = Note.pitchClass(note);
  for (const [voice, voiceNote] of Object.entries(voices)) {
    if (Note.pitchClass(voiceNote) === noteClass) {
      return voice as SuspensionVoice;
    }
  }
  return null;
}

/**
 * Calculate if a voice can create a valid suspension of the given type
 */
function canVoiceCreateSuspension(
  voice: SuspensionVoice,
  sourceChord: Chord,
  targetChord: Chord,
  suspensionType: SuspensionType
): SuspensionPlacement | null {
  const config = SUSPENSION_CONFIGS[suspensionType];
  const sourceNote = sourceChord.voices[voice];
  const targetBass = targetChord.voices.bass;

  // Calculate interval from target bass to source note
  const interval = getIntervalSemitones(targetBass, sourceNote);

  // Check if interval matches required suspension interval (allow some flexibility)
  const expectedIntervals = getExpectedIntervals(suspensionType);
  if (!expectedIntervals.includes(interval)) {
    return null;
  }

  // Calculate resolution note
  let resolutionNote: string;
  if (config.resolutionDirection === 'down') {
    // Try half step first, then whole step
    resolutionNote = stepDown(sourceNote, 1);
    if (!noteInChord(resolutionNote, targetChord.voices)) {
      resolutionNote = stepDown(sourceNote, 2);
    }
  } else {
    // Retardation (2-3): resolve up
    resolutionNote = stepUp(sourceNote, 1);
    if (!noteInChord(resolutionNote, targetChord.voices)) {
      resolutionNote = stepUp(sourceNote, 2);
    }
  }

  // Verify resolution note exists in target chord
  if (!noteInChord(resolutionNote, targetChord.voices)) {
    return null;
  }

  return {
    suspensionType,
    suspendedVoice: voice,
    preparationNote: sourceNote,
    suspensionNote: sourceNote,  // Same note held over
    resolutionNote,
  };
}

/**
 * Get expected intervals for a suspension type
 * Returns array of valid semitone intervals from bass
 */
function getExpectedIntervals(suspensionType: SuspensionType): number[] {
  switch (suspensionType) {
    case '4-3':
      return [5];  // Perfect 4th
    case '7-6':
      return [10, 11];  // Minor 7th or Major 7th
    case '9-8':
      return [1, 2];  // Minor 2nd or Major 2nd (9th reduced to octave)
    case '2-3':
      return [1, 2];  // Minor 2nd or Major 2nd
    default:
      return [];
  }
}

/**
 * Calculate the best suspension placement for a chord pair
 * Returns null if no valid suspension is possible
 */
export function calculateSuspensionPlacement(
  sourceChord: Chord,
  targetChord: Chord,
  suspensionType: SuspensionType
): SuspensionPlacement | null {
  // Try each voice in order of preference
  for (const voice of VOICE_PREFERENCE) {
    const placement = canVoiceCreateSuspension(
      voice,
      sourceChord,
      targetChord,
      suspensionType
    );
    if (placement) {
      return placement;
    }
  }

  return null;
}

/**
 * Validate if a suspension can be placed between two chords
 */
export function validateSuspensionPlacement(
  sourceChord: Chord,
  targetChord: Chord,
  suspensionType: SuspensionType
): SuspensionValidation {
  const placement = calculateSuspensionPlacement(
    sourceChord,
    targetChord,
    suspensionType
  );

  if (placement) {
    return {
      valid: true,
      placement,
    };
  }

  // Provide helpful reason
  const config = SUSPENSION_CONFIGS[suspensionType];
  return {
    valid: false,
    reason: `No voice in the first chord can create a ${config.label} suspension against the second chord. Try a different suspension type or chord pair.`,
  };
}

/**
 * Check all suspension types and return which ones are valid
 * for a given chord pair
 */
export function getValidSuspensionTypes(
  sourceChord: Chord,
  targetChord: Chord
): SuspensionType[] {
  const validTypes: SuspensionType[] = [];

  for (const suspensionType of ['4-3', '7-6', '9-8', '2-3'] as SuspensionType[]) {
    const validation = validateSuspensionPlacement(
      sourceChord,
      targetChord,
      suspensionType
    );
    if (validation.valid) {
      validTypes.push(suspensionType);
    }
  }

  return validTypes;
}

/**
 * Get a description of the suspension for educational display
 */
export function describeSuspension(placement: SuspensionPlacement): string {
  const config = SUSPENSION_CONFIGS[placement.suspensionType];
  const direction = config.resolutionDirection === 'down' ? 'down to' : 'up to';

  return `The ${placement.suspendedVoice} holds ${placement.preparationNote}, ` +
    `creating the suspended ${config.label.split('→')[0]} against the bass, ` +
    `then resolves ${direction} ${placement.resolutionNote}.`;
}
